import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardMedia from '@mui/material/CardMedia'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import {
  PERMISSIONS,
  airworthinessLight,
  worstNavDataState,
  equipmentHasPbn,
  inMaintenance,
  formatMinutes,
  formatUnit,
  unitLabel,
  type AircraftMaintenance,
  type AircraftPerformance,
  type ProfileAircraft,
  buildAircraftProfile,
  skydemonAircraftXml,
  type AirworthinessLight,
} from '@jupi/shared'
import { EnvelopeChart, MaintenanceBar, NavDataCard, NavDataChip, RemoteContent, Title, UnitSystemToggle, aircraftPictureUrl, formatDate, useAuth, useClubToday, useNavData, useUnitSystem } from '@jupi/ui'
import {
  useAircraft,
  useAircraftDefects,
  useAircraftDocuments,
  useAircraftFlights,
  useAircraftFlightManual,
  useAircraftPowerSettings,
  useMaintenanceEvents,
  useAircraftPerformance,
  useMaintenanceSchedules,
} from '../api/queries'
import AircraftDentsCard from '../components/AircraftDentsCard'
import { AircraftStats, DocumentsCard, FuelGauge, FlightManualCard, FuelCardPin, PohCard, PowerSettingsCard, RecentFlightsCard } from '../components/AircraftCards'

const LIGHT_COLOUR: Record<AirworthinessLight, 'error' | 'warning' | 'success'> = {
  aog: 'error',
  restricted: 'warning',
  airworthy: 'success',
}

// Planedata.php: the aircraft spec sheet and its booking rules, plus the
// airworthiness paperwork the flight school tracked (flight.aircraft).
// Mobile first: one column, in the order a pilot at the apron needs it
// (status, handbook, documents, operating data, recent flights, booking
// rules); two columns from md up.
export default function AircraftPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { registration } = useParams()
  const { data: aircraft = [], isLoading, error } = useAircraft()
  const plane = aircraft.find((a) => a.registration === registration?.toUpperCase())
  const { data: performance } = useAircraftPerformance(plane?.registration)
  const { data: documents } = useAircraftDocuments(plane?.registration)
  const { data: flights } = useAircraftFlights(plane?.registration)
  const { data: defects } = useAircraftDefects()
  const { data: powerSettings } = useAircraftPowerSettings(plane?.registration)
  const { data: flightManual } = useAircraftFlightManual(plane?.registration)
  const { data: maintenanceEvents = [] } = useMaintenanceEvents()
  const today = useClubToday()
  const planeEvents = maintenanceEvents.filter((e) => e.registration === plane?.registration)
  const inHangar = inMaintenance(planeEvents, today)
  const pictureUrl = plane ? aircraftPictureUrl(plane.registration, plane.pictureUpdatedAt) : null
  const { can } = useAuth()
  const system = useUnitSystem()
  const { data: maintenance = [] } = useMaintenanceSchedules(can(PERMISSIONS.maintenanceRead))
  const upkeep = maintenance.find((m) => m.registration === plane?.registration)
  const { data: navUnits = [] } = useNavData(plane?.registration)
  const navState = worstNavDataState(navUnits)
  const light = airworthinessLight((defects?.published ?? []).filter((d) => d.registration === plane?.registration))

  return (
    <Box>
      <Title label={plane?.registration ?? t('aircraft.title')}>
        {plane && performance ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<Icon icon="mdi:download" />}
            onClick={() => downloadSkydemon(plane, performance)}
            sx={{ minHeight: 32 }}
          >
            {t('performance.exportSkydemon')}
          </Button>
        ) : null}
      </Title>
      <RemoteContent isLoading={isLoading} error={error}>
        {plane ? (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              alignItems: 'start',
              '& > .wide': { gridColumn: { md: '1 / -1' } },
            }}
          >
            <Card className="wide">
              {pictureUrl ? (
                <CardMedia
                  component="img"
                  image={pictureUrl}
                  alt={t('aircraft.picture.alt', { registration: plane.registration })}
                  sx={{ aspectRatio: '16 / 9', objectFit: 'cover', maxHeight: 360 }}
                />
              ) : null}
              <CardContent>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1, mb: 2 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h5" component="h2">
                      {plane.registration}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {plane.aircraftType}
                    </Typography>
                  </Box>
                  {!plane.available ? <Chip color="error" label={t('aircraft.unavailable')} /> : null}
                  {inHangar ? (
                    <Chip
                      icon={<Icon icon="mdi:wrench" />}
                      label={t('aircraft.inMaintenance')}
                      sx={{ fontWeight: 700, bgcolor: 'grey.700', color: 'common.white', '& .MuiChip-icon': { color: 'inherit' } }}
                    />
                  ) : null}
                  {navState && navState !== 'current' ? <NavDataChip state={navState} /> : null}
                  <Chip
                    clickable={light !== 'airworthy'}
                    color={LIGHT_COLOUR[light]}
                    icon={<Icon icon={light === 'airworthy' ? 'mdi:check-circle-outline' : light === 'aog' ? 'mdi:hand-front-left' : 'mdi:alert-outline'} />}
                    label={t(`aircraftDefect.airworthiness.${light}`)}
                    onClick={light !== 'airworthy' ? () => navigate(`/fleet/defects?registration=${plane.registration}`) : undefined}
                    sx={{ fontWeight: 700 }}
                  />
                </Stack>
                {inHangar ? (
                  <Alert severity="info" icon={<Icon icon="mdi:wrench" />} sx={{ mb: 2 }}>
                    {planeEvents
                      .filter((e) => e.startsOn <= today)
                      .map((e) => t('aircraft.maintenanceUntil', { until: formatDate(`${e.endsOn}T12:00:00Z`) }) + (e.note ? ` ${e.note}` : ''))
                      .join(' ')}
                  </Alert>
                ) : planeEvents.length > 0 ? (
                  <Alert severity="info" icon={<Icon icon="mdi:wrench-clock" />} sx={{ mb: 2 }}>
                    {planeEvents
                      .map((e) =>
                        t('aircraft.maintenanceFrom', { from: formatDate(`${e.startsOn}T12:00:00Z`), until: formatDate(`${e.endsOn}T12:00:00Z`) }),
                      )
                      .join(' ')}
                  </Alert>
                ) : null}
                <AircraftStats totals={plane.totals} />
                {plane.fuel ? (
                  <Box sx={{ mt: 2 }}>
                    <FuelGauge fuel={plane.fuel} />
                  </Box>
                ) : null}
                {plane.comment ? (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2">{plane.comment}</Typography>
                  </>
                ) : null}
              </CardContent>
            </Card>

            <Box className="wide">
              <PohCard registration={plane.registration} documents={documents} />
            </Box>

            {plane.hasFuelCardPin ? (
              <Box className="wide">
                <FuelCardPin registration={plane.registration} />
              </Box>
            ) : null}

            {upkeep?.schedule ? <MaintenanceCard upkeep={upkeep} /> : null}

            <NavDataCard registration={plane.registration} hasGps={plane.hasGps} canEdit={can(PERMISSIONS.maintenanceWrite)} />

            <Box className="wide">
              <AircraftDentsCard registration={plane.registration} />
            </Box>

            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('aircraft.operatingInfo')}
                </Typography>
                <Stack spacing={1}>
                  <Spec label={t('aircraft.approvedFuels')} value={plane.approvedFuels} />
                  <Spec label={t('aircraft.equipment')} value={plane.equipment} />
                  <Spec label={t('aircraft.transponderMode')} value={plane.transponderMode} />
                  {equipmentHasPbn(plane.equipment) ? <Spec label={t('aircraft.pbn')} value={plane.pbn.join(' ')} /> : null}
                  <Spec label={t('aircraft.seats')} value={plane.seats} />
                  <Spec label={t('aircraft.engineType')} value={plane.engineType} />
                  <Spec label={t('aircraft.fuelConsumptionUsg')} value={plane.fuelConsumptionUsg} unit="USG/h" />
                  <Spec
                    label={t('aircraft.mtow')}
                    value={plane.mtow === null ? null : Number(formatUnit('mass', plane.mtow, system))}
                    unit={unitLabel('mass', system)}
                  />
                  <Spec label={t('aircraft.serviceCeiling')} value={plane.serviceCeiling} unit="ft" />
                  <Spec label={t('aircraft.maxRange')} value={plane.maxRange} unit="km" />
                  <Spec label={t('aircraft.stc')} value={plane.stc} />
                </Stack>
              </CardContent>
            </Card>

            {documents ? (
              <Box className="wide">
                <DocumentsCard registration={plane.registration} documents={documents} />
              </Box>
            ) : null}

            {powerSettings && powerSettings.length > 0 ? <PowerSettingsCard settings={powerSettings} /> : null}

            {flights ? <RecentFlightsCard flights={flights} /> : null}

            

            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('aircraft.rules')}
                </Typography>
                <Stack spacing={1}>
                  {plane.highSeasonApply && plane.highSeasonStart && plane.highSeasonEnd ? (
                    <>
                      <Typography variant="subtitle2">
                        {t('aircraft.highSeason')}: {formatDate(`${plane.highSeasonStart}T12:00:00Z`)} –{' '}
                        {formatDate(`${plane.highSeasonEnd}T12:00:00Z`)}
                      </Typography>
                      <Spec label={t('aircraft.maxDays')} value={plane.highSeasonDays} unit={t('aircraft.days')} />
                      <Spec
                        label={t('aircraft.maxAdvance')}
                        value={plane.highSeasonAdvance}
                        unit={t('aircraft.days')}
                      />
                      <Divider sx={{ my: 1 }} />
                    </>
                  ) : null}
                  <Spec label={t('aircraft.maxDays')} value={plane.lowSeasonDays} unit={t('aircraft.days')} />
                  <Spec label={t('aircraft.maxAdvance')} value={plane.lowSeasonAdvance} unit={t('aircraft.days')} />
                </Stack>
              </CardContent>
            </Card>

            {performance === undefined ? null : (
              <Box className="wide">
                <PerformanceCard performance={performance} />
              </Box>
            )}

            {flightManual ? (
              <Box className="wide">
                <FlightManualCard speeds={flightManual.speeds} cruise={flightManual.cruise} />
              </Box>
            ) : null}

            {plane.afmRevision || plane.checklistRevision ? (
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {t('aircraft.manuals')}
                  </Typography>
                  <Stack spacing={1}>
                    <Spec label={t('aircraft.afmRevision')} value={plane.afmRevision} />
                    <Spec label={t('aircraft.checklistRevision')} value={plane.checklistRevision} />
                  </Stack>
                </CardContent>
              </Card>
            ) : null}

            {/* Carried over from Planedata.php, which ended with this in
                bold. It is a safety statement, not boilerplate. */}
            <Alert severity="warning" className="wide">
              {t('aircraft.disclaimer')}
            </Alert>
          </Box>
        ) : null}
      </RemoteContent>
    </Box>
  )
}

function Spec({
  label,
  value,
  unit,
}: {
  label: string
  value: string | number | null | undefined
  unit?: string
}) {
  if (value === null || value === undefined || value === '') return null
  return (
    <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value}
        {unit ? ` ${unit}` : ''}
      </Typography>
    </Stack>
  )
}

const SPEED_UNIT_LABEL = { kt: 'kt', mph: 'mph', kmh: 'km/h' } as const
const DESCRIPTIVE_SPEEDS = [
  'stallSpeedFlaps',
  'stallSpeedClean',
  'approachSpeed',
  'bestGlideSpeed',
  'bestClimbSpeed',
  'steepestClimbSpeed',
] as const
const ARMS = ['armBasicMass', 'armFrontPax', 'armRearPax', 'armCargo', 'armFuelMain', 'armFuelRes'] as const

// flight.aircraft_performance: what the flight planner computes with. The
// descriptive speeds and the runway figure are the flight manual's own
// wording, shown as entered.
// The next maintenance and the hours left before it, from the schedule the
// fleet manager, CAMO or maintenance staff keep, and the last maintenance
// performed.
function MaintenanceCard({ upkeep }: { upkeep: AircraftMaintenance }) {
  const { t } = useTranslation()
  const { schedule, status, lastRecord, airframeMinutes } = upkeep
  if (!schedule) return null
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('maintenance.scheduleTitle')}
        </Typography>
        {status ? (
          <MaintenanceBar schedule={schedule} status={status} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('maintenance.noAirframeHours')}
          </Typography>
        )}
        <Stack spacing={1} sx={{ mt: 2 }}>
          {airframeMinutes != null ? <Spec label={t('maintenance.airframeHours')} value={formatMinutes(airframeMinutes)} /> : null}
          {lastRecord ? (
            <Spec
              label={t('maintenance.lastMaintenance')}
              value={`${lastRecord.maintenanceType} · ${formatDate(`${lastRecord.performedOn}T12:00:00Z`)} · ${formatMinutes(lastRecord.airframeMinutes)}`}
            />
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  )
}

// The aircraft as a SkyDemon ".aircraft" file, from the figures this card shows.
function downloadSkydemon(aircraft: ProfileAircraft, performance: AircraftPerformance) {
  const xml = skydemonAircraftXml(buildAircraftProfile(aircraft, performance))
  const url = URL.createObjectURL(new Blob([xml], { type: 'application/xml' }))
  const link = document.createElement('a')
  link.href = url
  link.download = `${aircraft.registration}.aircraft`
  link.click()
  URL.revokeObjectURL(url)
}

function PerformanceCard({ performance }: { performance: AircraftPerformance | null }) {
  const { t } = useTranslation()
  const system = useUnitSystem()
  if (!performance) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('performance.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('performance.notEntered')}
          </Typography>
        </CardContent>
      </Card>
    )
  }
  const speedUnit = SPEED_UNIT_LABEL[performance.speedUnit]
  const mass = (kg: number) => formatUnit('mass', kg, system)
  const fuel = (usg: number) => formatUnit('fuel', usg, system)
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 1, mb: 2 }}>
          <Typography variant="h6">{t('performance.title')}</Typography>
          <UnitSystemToggle />
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 3, md: 5 },
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            alignItems: 'start',
          }}
        >
        <Stack spacing={1}>
          <Spec label={t('performance.emptyWeight')} value={mass(performance.emptyWeightKg)} unit={unitLabel('mass', system)} />
          <Spec label={t('performance.fuelMain')} value={fuel(performance.maxFuelMainUsg)} unit={unitLabel('fuel', system)} />
          {performance.maxFuelResUsg > 0 ? (
            <Spec label={t('performance.fuelRes')} value={fuel(performance.maxFuelResUsg)} unit={unitLabel('fuel', system)} />
          ) : null}
          <Spec label={t('performance.unusableFuel')} value={fuel(performance.unusableFuelUsg)} unit={unitLabel('fuel', system)} />
          <Divider sx={{ my: 1 }} />
          <Spec label={t('performance.cruiseSpeed')} value={performance.cruiseSpeed} unit={speedUnit} />
          <Spec label={t('performance.manualCruiseSpeed')} value={performance.manualCruiseSpeed} unit={speedUnit} />
          {DESCRIPTIVE_SPEEDS.map((key) => (
            // The manual's own wording ("70 (61 lyhyt rata)") gets no unit
            // tacked on; a bare figure does.
            <Spec
              key={key}
              label={t(`performance.${key}`)}
              value={performance[key]}
              unit={/^\d+([.,]\d+)?$/.test(performance[key] ?? '') ? speedUnit : undefined}
            />
          ))}
          <Spec label={t('performance.runwayRequirement')} value={performance.runwayRequirement} />
          <Spec label={t('performance.crosswindLimit')} value={performance.crosswindLimitKt} unit="kt" />
          {performance.engineRatedBhp != null ? (
            <Spec
              label={t('performance.engineRated')}
              value={
                performance.engineRatedRpm != null
                  ? t('performance.engineRatedValue', { bhp: performance.engineRatedBhp, rpm: performance.engineRatedRpm })
                  : `${performance.engineRatedBhp} BHP`
              }
            />
          ) : null}
          <Spec label={t('performance.engineMaxContinuous')} value={performance.engineMaxContinuousRpm} unit="RPM" />
        </Stack>

        <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('units.arms', { unit: unitLabel('arm', system) })}
        </Typography>
        <Table size="small">
          <TableBody>
            {ARMS.filter((key) => key !== 'armRearPax' || performance.hasRearSeats).map((key) => (
              <TableRow key={key}>
                <TableCell sx={{ pl: 0 }}>{t(`performance.${key}`)}</TableCell>
                <TableCell align="right" sx={{ pr: 0, fontVariantNumeric: 'tabular-nums' }}>
                  {formatUnit('arm', performance[key], system)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
          {t('performance.envelope')}
        </Typography>
        <EnvelopeChart envelope={performance.cgEnvelope} />
        </Box>
        </Box>
      </CardContent>
    </Card>
  )
}
